/**
 * Покупки за Telegram Stars (MADR 0003): товары, цены, таймер после падения штаба. Числа здесь, а не в балансе: журналы
 * волн от них не зависят, смена цены не требует новой версии баланса.
 */
import { z } from "zod";
import { BoostRarity } from "./balance";
import { ContentId, PosInt, UInt, UnixMs } from "./common";

/** Подписка VIP: цена за период в Stars. */
export const VIP_STARS = 199;
/** Период подписки. Telegram принимает только это значение. */
export const VIP_PERIOD_SECONDS = 2_592_000;
/** Запас к дате окончания от Telegram: продление может прийти позже неё. */
export const VIP_GRACE_MS = 6 * 3_600_000;
/** Пропуск одного таймера, Stars. */
export const SKIP_STARS = 5;

/** Пропуск сезона: разовая покупка на один сезон, Stars. */
export const SEASON_PASS_STARS = 99;

/** Стартовый пакет (MADR 0014): один раз на игрока. Монеты — столько наград за лучшую волну, VIP — дней без продления. */
export const STARTER_STARS = 49;
export const STARTER_BAG_WAVES = 100;
export const STARTER_VIP_DAYS = 3;

/** Таймер после падения штаба: с какой волны и сколько длится. */
export const RETRY_LOCK_FROM_WAVE = 5;
export const RETRY_LOCK_MS = 30_000;

/** Реванш после падения штаба (MADR 0007): цена по номеру реванша на волне; длина — лимит на волну. */
export const REVIVE_STARS = [10, 25] as const;
export const REVIVE_LIMIT = REVIVE_STARS.length;
/** Сколько реваншей на волну VIP получает бесплатно: первые по порядку. */
export const REVIVE_FREE_FOR_VIP = 1;
/** Подкрепление реванша (MADR 0019): сколько усилений игрок выбирает по очереди перед повтором волны. */
export const REVIVE_BOOST_PICKS = 2;

/** Что продаём. */
export const Product = z.enum(["vip", "skip", "season", "revive", "starter"]);
export type Product = z.infer<typeof Product>;

/** Цена товара с постоянной ценой, Stars. Цена реванша — {@link reviveStars}. */
export const PRODUCT_STARS: Record<Exclude<Product, "revive">, number> = {
  vip: VIP_STARS,
  skip: SKIP_STARS,
  season: SEASON_PASS_STARS,
  starter: STARTER_STARS,
};

/** Цена реванша с номером `attempt` (с 1), Stars; 0 — бесплатно. Вне лимита — `null`. */
export function reviveStars(attempt: number, vip: boolean): number | null {
  const stars = REVIVE_STARS[attempt - 1];
  if (stars === undefined) return null;
  return vip && attempt <= REVIVE_FREE_FOR_VIP ? 0 : stars;
}

/** Цена любого платного товара для аккаунтов из `ADMIN_TELEGRAM_IDS`: проверка настоящей покупки на проде (ISSUE-111). */
export const TEST_PRICE_STARS = 1;

/** Цена с учётом тестовой: бесплатное остаётся бесплатным, у Telegram нет счёта на 0 Stars. */
export const priceStars = (stars: number, testPrice: boolean): number =>
  testPrice && stars > 0 ? TEST_PRICE_STARS : stars;

/** Цены товаров, которые видит игрок. Реванш и пакет несут цену в своих предложениях. */
export const StarPrices = z.object({ vip: PosInt, skip: PosInt, season: PosInt });
export type StarPrices = z.infer<typeof StarPrices>;

export const starPrices = (testPrice: boolean): StarPrices => ({
  vip: priceStars(VIP_STARS, testPrice),
  skip: priceStars(SKIP_STARS, testPrice),
  season: priceStars(SEASON_PASS_STARS, testPrice),
});

/** VIP игрока. `until` — конец срока с запасом. */
export const VipState = z.object({
  active: z.boolean(),
  until: UnixMs.nullable(),
});
export type VipState = z.infer<typeof VipState>;

export const NO_VIP: VipState = { active: false, until: null };

/** Таймер: до какого момента нельзя начать бой и на какой волне пал штаб. */
export const RetryLock = z.object({
  until: UnixMs,
  wave: PosInt,
});
export type RetryLock = z.infer<typeof RetryLock>;

/** Предложение реванша: считает сервер, цена и что вернётся видны до покупки. */
export const ReviveOffer = z.object({
  /** Волна, которая начнётся заново. */
  wave: PosInt,
  /** Какой по счёту реванш на этой волне, с 1. */
  attempt: PosInt,
  /** Цена в Stars; 0 — бесплатно для VIP. */
  stars: z.number().int().nonnegative(),
  /** Усиления, которые останутся после реванша и сгорят без него. */
  boosts: z.array(z.string()),
});
export type ReviveOffer = z.infer<typeof ReviveOffer>;

/** Вариант «1 из 3» — как `BoostOption` движка: позиция, усиление, редкость, взятые стаки и потолок (`null` — бесконечное). */
export const BoostOption = z.object({
  index: UInt,
  boostId: ContentId,
  rarity: BoostRarity,
  stacks: UInt,
  maxStacks: PosInt.nullable(),
});
export type BoostOption = z.infer<typeof BoostOption>;

/** Подкрепление после реванша (MADR 0019): осталось выбрать `left` из `of`, текущее предложение — `options`. */
export const RevivePicks = z.object({
  left: PosInt,
  of: PosInt,
  options: z.array(BoostOption).min(1),
});
export type RevivePicks = z.infer<typeof RevivePicks>;

/** Выбор подкрепления: позиция и усиление на ней (сверка с предложением сервера, как у `chooseBoost`). */
export const ReviveBoostRequest = z.object({
  index: z.number().int().min(0).max(2),
  boostId: ContentId,
});
export type ReviveBoostRequest = z.infer<typeof ReviveBoostRequest>;

/** Предложение стартового пакета: состав и цена видны до покупки. */
export const StarterOffer = z.object({
  stars: PosInt,
  coins: PosInt,
  vipDays: PosInt,
});
export type StarterOffer = z.infer<typeof StarterOffer>;

/** Таймер после проигрыша; `null` — без таймера (ранняя волна или VIP). */
export function retryLockAfterLoss(wave: number, vip: boolean, nowMs: number): RetryLock | null {
  if (vip || wave < RETRY_LOCK_FROM_WAVE) return null;
  return { until: nowMs + RETRY_LOCK_MS, wave };
}
